import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'environments/environment';
import {
  IPaginatedListViewSearchModel,
  KeyValuePairModel,
  PaginatedModel,
} from 'app/shared/models/common.models';
import { FilterParameterService } from 'app/shared/services/filter-parameter.service';

import {
  ICreateOutboundTransitUnitModel,
  IGetOutboundFilterModel,
  IOutboundAlternativePickupDetailsModel,
  IOutboundBottleModel,
  IOutboundDetailsModel,
  IOutboundListItemModel,
  IOutboundLookupItemModel,
  IOutboundNotesUpdateModel,
  IOutboundTransitUnitModel,
  IOutbountTransitUnitSummaryModel,
  IUpdateOutboundLocationModel,
  IUpdateTransitUnitCourierDetails,
  IValidateOutboundResponseModel,
} from '../models/outbound.models';
import { ApiRequestFilters } from '../models/api-request.model';
import { GlobalFilterService } from './global-filter-state.service';

@Injectable({
  providedIn: 'root',
})
export class LogisticsOutboundService {
  private searchEvent = new Subject<any>();

  searchEvent$ = this.searchEvent.asObservable();

  private drawerData: Subject<EditDrawerData> =
    new BehaviorSubject<EditDrawerData>(null);

  get drawerData$() {
    return this.drawerData.asObservable();
  }

  private get baseApiUrl(): string {
    return environment.logisticsUrl;
  }

  constructor(
    private http: HttpClient,
    private filterParameterService: FilterParameterService,
    private globalFilterService: GlobalFilterService
  ) {}

  getOutboundsV1(
    filters?: ApiRequestFilters
  ): Observable<PaginatedModel<IOutboundListItemModel>> {
    const url = `${this.baseApiUrl}/v1/Outbound`;

    const params = this.filterParameterService.toHttpParams({
      ...filters,
      sites: this.getGlobalSitesFilter(),
    });

    return this.http.get<PaginatedModel<IOutboundListItemModel>>(url, {
      params,
    });
  }

  getOutboundsByCheckOutIdV1(
    checkOutId: number,
    count: number
  ): Observable<PaginatedModel<IOutboundLookupItemModel>> {
    const url = `${this.baseApiUrl}/v1/Outbound/Lookup`;
    const params = new HttpParams()
      .append('checkOutId', checkOutId.toString())
      .append('pageSize', count.toString());

    return this.http.get<PaginatedModel<IOutboundLookupItemModel>>(url, {
      params,
    });
  }

  getOutboundDetailsV1(id: number): Observable<IOutboundDetailsModel> {
    const url = `${this.baseApiUrl}/V1/Outbound/${id}`;
    return this.http.get<IOutboundDetailsModel>(url, {});
  }

  getOutboundTransitUnitDetailByLabelCodeV1(
    labelCode: string
  ): Observable<IOutbountTransitUnitSummaryModel> {
    const url = `${this.baseApiUrl}/V1/Outbound/TransitUnits/${labelCode}`;
    return this.http.get<IOutbountTransitUnitSummaryModel>(url, {});
  }

  getOutboundBottlesV1(
    outboundId: number,
    searchModel: IPaginatedListViewSearchModel,
    awaitingPackingOnly: boolean
  ): Observable<PaginatedModel<IOutboundBottleModel>> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/Bottles`;
    const params = new HttpParams()
      .append('filterText', searchModel.searchText)
      .append('orderByColumn', searchModel.orderByColumn)
      .append('orderByDirection', searchModel.orderByDirection)
      .append('page', searchModel.page.toString())
      .append('pageSize', searchModel.pageSize.toString())
      .append('awaitingPackingOnly', awaitingPackingOnly.toString());

    return this.http.get<PaginatedModel<IOutboundBottleModel>>(url, {
      params,
    });
  }

  getOutboundTransitUnitsV1(
    outboundId: number,
    searchModel: IPaginatedListViewSearchModel
  ): Observable<PaginatedModel<IOutboundTransitUnitModel>> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/TransitUnits`;
    const params = new HttpParams()
      .append('filterText', searchModel.searchText)
      .append('orderByColumn', searchModel.orderByColumn)
      .append('orderByDirection', searchModel.orderByDirection)
      .append('page', searchModel.page.toString())
      .append('pageSize', searchModel.pageSize.toString());

    return this.http.get<PaginatedModel<IOutboundTransitUnitModel>>(url, {
      params,
    });
  }

  createTransitUnitV1(
    outboundId: number,
    model: ICreateOutboundTransitUnitModel
  ): Observable<number[]> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/TransitUnits`;
    return this.http
      .post<{ transitUnits: number[] }>(url, model)
      .pipe(map((x) => x.transitUnits));
  }

  deleteTransitUnitV1(
    outboundId: number,
    transitUnitId: number
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/TransitUnits/${transitUnitId}`;
    return this.http.delete<void>(url);
  }

  updateTransitUnitCourierDetailsV1(
    outboundId: number,
    transitUnitId: number,
    model: IUpdateTransitUnitCourierDetails
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/TransitUnits/${transitUnitId}/CourierDetails`;
    return this.http.put<void>(url, model);
  }

  finishPackingOutboundV1(outboundId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/FinishPacking`;
    return this.http.put<void>(url, {});
  }

  resumePackingOutboundV1(outboundId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/ResumePacking`;
    return this.http.put<void>(url, {});
  }

  validateOutboundV1(
    outboundId: number
  ): Observable<IValidateOutboundResponseModel> {
    const url = `${this.baseApiUrl}/V1/Outbound/${outboundId}/Validate`;
    return this.http.get<IValidateOutboundResponseModel>(url);
  }

  approveAndCompleteOutboundV1(outboundId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/Approve`;
    return this.http.put<void>(url, {});
  }

  updateOutboundNotesV1(
    id: number,
    model: IOutboundNotesUpdateModel
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${id}/Notes`;
    return this.http.put<void>(url, model);
  }

  updateOutboundPickupDetailsV1(
    id: number,
    model: IOutboundAlternativePickupDetailsModel
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${id}/AlternativePickupDetails`;
    return this.http.put<void>(url, model);
  }

  getOutboundStatusBadgeClassName(status: string): string {
    const classBase = 'chip chip-';
    if (status) {
      switch (this.removeSpaces(status)) {
        case 'New': {
          return classBase + 'info-strong';
        }
        case 'Complete': {
          return classBase + 'success-strong';
        }
        default: {
          return classBase + 'action-strong';
        }
      }
    }
    return '';
  }

  updateOutboundLocationV1(
    outboundId: number,
    model: IUpdateOutboundLocationModel
  ): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/${outboundId}/Location`;
    return this.http.put<void>(url, model);
  }

  getPrintLabelUrl(): string {
    return `${environment.logisticsUrl}/v1/Outbound/TransitUnits/labels`;
  }

  getOutboundProcessingStatusesV1(
    filterText: string
  ): Observable<KeyValuePairModel<number>[]> {
    const url = `${this.baseApiUrl}/v1/Outbound/processingstatuses`;
    const params = new HttpParams().append('filterText', filterText);
    return this.http.get<KeyValuePairModel<number>[]>(url, { params });
  }

  getOutboundDeliveryTypesV1(
    filterText: string
  ): Observable<KeyValuePairModel<number>[]> {
    const url = `${this.baseApiUrl}/v1/Outbound/deliverytypes`;
    const params = new HttpParams().append('filterText', filterText);
    return this.http.get<KeyValuePairModel<number>[]>(url, { params });
  }

  private removeSpaces(value: string): string {
    return value.replace(/\s/g, '');
  }

  setDrawerData(type: EditType, transitUnit?: IOutboundTransitUnitModel) {
    this.drawerData.next({ type, transitUnit });
  }

  removeTransferStep(outboundId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/RemoveIntermediateTransfer/${outboundId}`;
    return this.http.put<void>(url, {});
  }

  addTransferStep(outboundId: number, siteId: number): Observable<void> {
    const url = `${this.baseApiUrl}/v1/Outbound/AddTransferStep/${outboundId}/${siteId}`;
    return this.http.put<void>(url, {});
  }

  getOutboundFilters(): Observable<IGetOutboundFilterModel> {
    const url = `${this.baseApiUrl}/v1/Outbound/filters`;
    return this.http.get<IGetOutboundFilterModel>(url);
  }

  getGlobalSitesFilter = (): Object => {
    const siteIds = this.globalFilterService.getCachedSites();
    if (siteIds.length === 1 && siteIds.includes(0)) {
      return null;
    }

    return siteIds;
  };
}

export interface EditDrawerData {
  type: EditType;
  transitUnit: IOutboundTransitUnitModel;
}

export type EditType = 'notes' | 'location' | 'delivery' | 'courierDetails';
